'use es6';

import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import emptyFunction from 'react-utils/emptyFunction';
import { connect } from 'react-redux';
import { isEqual } from 'underscore';
import MenuRoot from 'simple-menus-lib/components/menu/MenuRoot';
import MenuDragLayer from 'simple-menus-lib/components/dnd/preview/MenuDragLayer';
import MenuEmptyState from 'simple-menus-lib/components/menu/MenuEmptyState';
import AddMenuButton from './AddMenuButton';
import UISection from 'UIComponents/section/UISection';
import * as MenuTreeActions from 'simple-menus-core/actions/MenuTree';
import * as menuTreeSelectors from 'simple-menus-core/selectors/menuTree';
import * as applicationSelectors from 'simple-menus-core/selectors/application';
import withErrorHandler from 'simple-menus-lib/components/menu/decorators/withErrorHandler';
import withUniqueMenuId from 'simple-menus-lib/components/menu/decorators/withUniqueMenuId';
import { compose } from 'redux';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function makeMapStateToProps() {
  const getMenuDraftpages = menuTreeSelectors.makeGetDraftPagesForMenu();
  const getCompleteMenuTree = menuTreeSelectors.makeGetCompleteMenuTree();
  const getMaxDepth = menuTreeSelectors.makeGetMaxMenuDepth();
  return function mapStateToProps(state, props) {
    return {
      completeTree: getCompleteMenuTree(state, props),
      isDragging: applicationSelectors.getIsDragging(state),
      maxDepth: getMaxDepth(state, props),
      menuDraftpages: getMenuDraftpages(state, props),
      tree: menuTreeSelectors.getMenuTree(state, props)
    };
  };
}
const mapDispatchToProps = {
  loadMenuTree: MenuTreeActions.loadMenuTree,
  moveNode: MenuTreeActions.moveNode,
  addNode: MenuTreeActions.addNode,
  deleteTree: MenuTreeActions.deleteTree
};
class Menu extends PureComponent {
  constructor(props) {
    super(props);
    this.addNode = this.addNode.bind(this);
    this.hasLoadedMenuTree = false;
  }
  componentDidMount() {
    const {
      loadMenuTree,
      menuDraftpages,
      onMenuHasDraftPages
    } = this.props;
    if (onMenuHasDraftPages) onMenuHasDraftPages(menuDraftpages);
    loadMenuTree(this.props.menuData, this.props.menuId);
  }
  componentDidUpdate(prevProps) {
    const {
      menuDraftpages,
      onMenuHasDraftPages,
      completeTree,
      onTreeChange
    } = this.props;
    if (onMenuHasDraftPages && prevProps.menuDraftpages !== menuDraftpages) {
      onMenuHasDraftPages(menuDraftpages);
    }

    // We basically want to "skip" firing an onTreeChange on the first prop
    // change that occurs in this component. It happens as a result of action
    // fired in componentDidMount that transforms menuData into completeTree.
    // No actual tree change happens, it's just "loaded" now, so don't make
    // upstream consumers of this component think that a change happened.
    if (this.hasLoadedMenuTree && !isEqual(prevProps.completeTree, completeTree)) {
      onTreeChange(completeTree);
    } else {
      this.hasLoadedMenuTree = true;
    }
  }
  get classNames() {
    return 'simple-menus-menu' + (this.props.isDragging ? " is--dragging" : "");
  }
  addNode() {
    this.props.addNode(this.props.menuId);
  }
  render() {
    const {
      tree,
      moveNode,
      menuId,
      maxDepth,
      readOnly
    } = this.props;
    if (!tree.length) {
      return /*#__PURE__*/_jsx(MenuEmptyState, {
        addNode: this.addNode,
        readOnly: readOnly
      });
    }
    return /*#__PURE__*/_jsxs(UISection, {
      className: this.classNames,
      children: [/*#__PURE__*/_jsx(MenuDragLayer, {}), /*#__PURE__*/_jsx(MenuRoot, {
        parentId: null,
        nodes: tree,
        moveNode: moveNode,
        menuId: menuId,
        maxDepth: maxDepth,
        readOnly: readOnly
      }), !readOnly && /*#__PURE__*/_jsx(AddMenuButton, {
        menuId: menuId
      })]
    });
  }
}
Menu.defaultProps = {
  tree: [],
  menuData: [],
  completeTree: [],
  onTreeChange: emptyFunction
};
Menu.propTypes = {
  tree: PropTypes.array.isRequired,
  loadMenuTree: PropTypes.func.isRequired,
  menuData: PropTypes.array.isRequired,
  menuDraftpages: PropTypes.array.isRequired,
  moveNode: PropTypes.func.isRequired,
  onMenuHasDraftPages: PropTypes.func,
  addNode: PropTypes.func.isRequired,
  completeTree: PropTypes.array.isRequired,
  onTreeChange: PropTypes.func.isRequired,
  menuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  maxDepth: PropTypes.number.isRequired,
  isDragging: PropTypes.bool,
  readOnly: PropTypes.bool
};
export default compose(withErrorHandler(), withUniqueMenuId(), connect(makeMapStateToProps, mapDispatchToProps))(Menu);